/**
 * Body renderer for each of the seven Management tabs. CORE-M-02..CORE-M-08
 * each swap in a real component for their slug; the rest remain
 * ticket-placeholder cards. Kept as a single dispatcher so the bar-plus-body
 * wiring stays in one file that a new reader can pick up without a
 * walkthrough (bus factor).
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { MANAGEMENT_TABS, type ManagementTabSlug } from './tabSlugs';
import { GoogleRecommendationsTab } from './GoogleRecommendationsTab';
import { NegativeKeywordSuggestionsTab } from './NegativeKeywordSuggestionsTab';
import { CampaignReviewTab } from './CampaignReviewTab';
import { AdGroupReviewTab } from './AdGroupReviewTab';
import { AdReviewTab } from './AdReviewTab';
import { KeywordReviewTab } from './KeywordReviewTab';
import { BudgetReallocationTab } from './BudgetReallocationTab';

interface ManagementTabBodyProps {
  slug: ManagementTabSlug;
  tenantId?: string | null;
}

const TAB_PURPOSE: Record<ManagementTabSlug, string> = {
  'google-recommendations':
    "Google's recommendations pulled into Core, with our own reasoning next to each one. Almost none should be applied as-is.",
  'negatives':
    "Search terms Core has flagged as bad matches, with the evidence that flagged them. Yes to add as a negative, No to keep it.",
  'campaign-review':
    'One row per live campaign, with the health checks Core runs before letting a campaign spend more.',
  'ad-group-review':
    'Ad groups worth a look, with what changed and why we noticed.',
  'ad-review':
    'Ads that need attention: disapproved, learning too long, or delivering less than the others in the same ad group.',
  'keyword-review':
    'Keywords Core wants to change, pause, or add, with the evidence per row.',
  'budget-reallocation':
    'Where Core suggests moving budget between live campaigns, with the reasoning per move.',
};

export function ManagementTabBody({ slug, tenantId }: ManagementTabBodyProps) {
  if (slug === 'google-recommendations') {
    return <GoogleRecommendationsTab tenantId={tenantId} />;
  }
  if (slug === 'negatives') {
    return <NegativeKeywordSuggestionsTab tenantId={tenantId} />;
  }
  if (slug === 'campaign-review') {
    return <CampaignReviewTab tenantId={tenantId} />;
  }
  if (slug === 'ad-group-review') {
    return <AdGroupReviewTab tenantId={tenantId} />;
  }
  if (slug === 'ad-review') {
    return <AdReviewTab tenantId={tenantId} />;
  }
  if (slug === 'keyword-review') {
    return <KeywordReviewTab tenantId={tenantId} />;
  }
  if (slug === 'budget-reallocation') {
    return <BudgetReallocationTab tenantId={tenantId} />;
  }

  const def = MANAGEMENT_TABS.find((t) => t.slug === slug);
  if (!def) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{def.label}</CardTitle>
        <CardDescription>{TAB_PURPOSE[slug]}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          The content for this tab is being built as ticket{' '}
          <span className="font-mono">{def.ticket}</span>. Until then, the
          current Management tools are shown below.
        </p>
      </CardContent>
    </Card>
  );
}

export default ManagementTabBody;
